import type { GlossaryFamily, GlossaryTerm } from "@unbox/api";
import { useEffect, useState } from "react";
import { useGlossary } from "@/api/ask";
import { useFocusTrap } from "@/pages/ask/hooks/useFocusTrap";
import Icon from "./Icon";

// ---------------------------------------------------------------------------
// Family accent colours (must match lib/glossary.ts FAMILY_ACCENTS)
// ---------------------------------------------------------------------------
const FAMILY_ACCENTS: Record<string, { accent: string; bg: string }> = {
  Creator: { accent: "var(--color-success-600)", bg: "var(--color-success-50)" },
  Content: { accent: "var(--color-warning-600)", bg: "var(--color-warning-50)" },
  Safety: { accent: "var(--color-error-600)", bg: "var(--color-error-50)" },
  Reporting: { accent: "var(--color-brand-600)", bg: "var(--color-brand-50)" },
  Adoption: { accent: "var(--color-gray-600)", bg: "var(--color-gray-100)" },
};

function familyAccent(family: string) {
  return FAMILY_ACCENTS[family] ?? { accent: "var(--color-gray-600)", bg: "var(--color-gray-100)" };
}

// ---------------------------------------------------------------------------
// TermCard — individual glossary entry
// ---------------------------------------------------------------------------
function TermCard({ term }: { term: GlossaryTerm }) {
  const [expanded, setExpanded] = useState(false);
  const { accent, bg } = familyAccent(term.family);

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid var(--color-border-secondary)",
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      {/* Main row */}
      <div style={{ padding: "12px 14px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
          <div
            style={{
              marginTop: 2,
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: accent,
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "var(--color-text-primary)",
                marginBottom: 4,
              }}
            >
              {term.term}
              {term.defaultWindow && (
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 11,
                    fontWeight: 500,
                    padding: "1px 7px",
                    borderRadius: 9999,
                    background: bg,
                    color: accent,
                    fontFamily: "var(--font-mono)",
                    verticalAlign: "middle",
                  }}
                >
                  {term.defaultWindow}
                </span>
              )}
            </div>

            <p
              style={{
                fontSize: 13,
                lineHeight: "19px",
                color: "var(--color-text-secondary)",
                margin: 0,
              }}
            >
              {term.definition}
            </p>

            {/* Aliases */}
            {term.aliases.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                  marginTop: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--color-text-quaternary)",
                    alignSelf: "center",
                  }}
                >
                  Also:
                </span>
                {term.aliases.map((alias) => (
                  <span
                    key={alias}
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      padding: "1px 7px",
                      borderRadius: 9999,
                      background: "var(--color-gray-100)",
                      color: "var(--color-text-tertiary)",
                      border: "1px solid var(--color-border-secondary)",
                    }}
                  >
                    {alias}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Why this matters — expandable */}
      {term.why && (
        <>
          <div
            style={{
              height: 1,
              background: "var(--color-border-tertiary)",
            }}
          />
          <button
            onClick={() => setExpanded((v) => !v)}
            style={{
              width: "100%",
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 12,
              fontWeight: 500,
              color: "var(--color-text-quaternary)",
              transition: "color 120ms",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "var(--color-text-secondary)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "var(--color-text-quaternary)";
            }}
          >
            <Icon name="chevronDown" size={12} color="currentColor" strokeWidth={2} />
            Why this definition
          </button>

          {expanded && (
            <div
              style={{
                padding: "0 14px 12px 14px",
                fontSize: 12,
                lineHeight: "18px",
                color: "var(--color-text-tertiary)",
                background: "var(--color-bg-secondary)",
                borderTop: "1px solid var(--color-border-tertiary)",
                paddingTop: 10,
              }}
            >
              {term.why}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FamilySection
// ---------------------------------------------------------------------------
function FamilySection({ family, terms }: { family: string; description: string; terms: GlossaryTerm[] }) {
  const { accent, bg } = familyAccent(family);

  return (
    <section style={{ marginBottom: 28 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            padding: "2px 8px",
            borderRadius: 9999,
            background: bg,
            color: accent,
          }}
        >
          {family}
        </span>
        <span
          style={{
            fontSize: 11,
            color: "var(--color-text-quaternary)",
            fontFamily: "var(--font-mono)",
          }}
        >
          {terms.length}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {terms.map((t) => (
          <TermCard key={t.term} term={t} />
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// GlossaryPanel
// ---------------------------------------------------------------------------
export default function GlossaryPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Fetched on first open, then cached for the session.
  const { data: families = [], isLoading: loading } = useGlossary(open);
  const [q, setQ] = useState("");
  const panelRef = useFocusTrap(open);
  const titleId = "glossary-panel-title";

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Filter terms by search query
  const ql = q.toLowerCase();
  const filteredFamilies: GlossaryFamily[] = families
    .map((f) => ({
      ...f,
      terms: q
        ? f.terms.filter(
            (t) =>
              t.term.toLowerCase().includes(ql) ||
              t.definition.toLowerCase().includes(ql) ||
              t.aliases.some((a) => a.toLowerCase().includes(ql)),
          )
        : f.terms,
    }))
    .filter((f) => f.terms.length > 0);

  const totalTerms = families.reduce((acc, f) => acc + f.terms.length, 0);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(10,13,18,0.12)",
          zIndex: 40,
        }}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(420px, 100vw)",
          background: "var(--color-bg-secondary)",
          borderLeft: "1px solid var(--color-border-secondary)",
          boxShadow: "var(--shadow-lg)",
          zIndex: 50,
          display: "flex",
          flexDirection: "column",
          outline: "none",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px 12px",
            background: "#fff",
            borderBottom: "1px solid var(--color-border-secondary)",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Icon name="bookOpen" size={18} color="var(--color-brand-700)" />
              <span
                id={titleId}
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: "var(--color-text-primary)",
                  letterSpacing: "-0.01em",
                }}
              >
                Glossary
              </span>
              {totalTerms > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--color-text-quaternary)",
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  {totalTerms} terms
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--color-gray-400)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--color-gray-100)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "none";
              }}
            >
              <Icon name="x" size={16} color="currentColor" strokeWidth={2} />
            </button>
          </div>

          {/* Search */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "7px 10px",
              background: "var(--color-bg-secondary)",
              border: "1px solid var(--color-border-secondary)",
              borderRadius: 8,
            }}
          >
            <Icon name="search" size={13} color="var(--color-gray-400)" />
            <input
              aria-label="Search definitions"
              placeholder="Search definitions…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{
                border: "none",
                outline: "none",
                background: "transparent",
                fontSize: 13,
                flex: 1,
                fontFamily: "inherit",
                color: "var(--color-text-secondary)",
              }}
            />
            {q && (
              <button
                onClick={() => setQ("")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--color-gray-400)",
                  display: "flex",
                  padding: 0,
                }}
              >
                <Icon name="x" size={12} color="currentColor" strokeWidth={2.5} />
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px 20px 32px",
          }}
        >
          {loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 120,
                color: "var(--color-text-quaternary)",
                fontSize: 13,
              }}
            >
              Loading…
            </div>
          ) : filteredFamilies.length === 0 ? (
            <div
              style={{
                padding: "48px 0",
                textAlign: "center",
                color: "var(--color-text-quaternary)",
                fontSize: 13,
              }}
            >
              No definitions match <strong style={{ color: "var(--color-text-tertiary)" }}>{q}</strong>.
            </div>
          ) : (
            filteredFamilies.map((f) => (
              <FamilySection key={f.family} family={f.family} description={f.description} terms={f.terms} />
            ))
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "10px 20px",
            borderTop: "1px solid var(--color-border-secondary)",
            background: "#fff",
            fontSize: 11,
            color: "var(--color-text-quaternary)",
            flexShrink: 0,
          }}
        >
          These definitions are applied automatically when you ask questions.
        </div>
      </div>
    </>
  );
}
